/**
 * Acquisitions (postclose) API — the `/loan_acquisitions` router.
 *
 * Only the endpoints that DON'T exist on the underwriting router live here.
 * Everything the two pipelines share (drilldown, sub-object CRUD, comments,
 * contacts, properties, borrowers, entities…) is served by `useUnderwritingHook`,
 * which re-points itself via PipelineContext.
 *
 * Backend: eastview-os frontend_documentation/loan-acquisitions.md
 */
import { losHttp } from "../lib/losHttp";

const BASE = "/loan_acquisitions";

export const losAcquisitionsApi = {
  // GET /acquisitions_pipeline → LoanAcquisition[] — each row carries
  // `entityName` (resolved; bind the entity column to THIS, not borrowerEntity),
  // `openConditionsCount`, `openExceptionsCount`, and the trade columns.
  // `investorCounterparty` is stripped server-side for non-capital-markets users.
  getAcquisitionsPipeline: () =>
    losHttp.get(`${BASE}/acquisitions_pipeline`).then((r) => r.data),

  // GET /trade/{tradeId}/loans → { tradeId, loanCount, loans: [...] }
  getTradeLoans: (tradeId) =>
    losHttp.get(`${BASE}/trade/${tradeId}/loans`).then((r) => r.data),

  // POST /link_trade_loans/{dealId} — recompute linkage across the trade
  linkTradeLoans: (dealId) =>
    losHttp.post(`${BASE}/link_trade_loans/${dealId}`, {}).then((r) => r.data),

  // ── Trade economics ────────────────────────────────────────────────────────
  getEconomics: (dealId) =>
    losHttp.get(`${BASE}/economics/${dealId}`).then((r) => r.data),

  setRtlBuyRate: (dealId, payload) =>
    losHttp
      .post(`${BASE}/economics/rtl_buy_rate/${dealId}`, payload)
      .then((r) => r.data),

  setDscrBondPoints: (dealId, payload) =>
    losHttp
      .post(`${BASE}/economics/dscr_bond_points/${dealId}`, payload)
      .then((r) => r.data),

  // ── Mortgagee clause (POPULATE from closed docs, then VALIDATE) ────────────
  // Postclose distinction: preclose SELECTS a mortgagee from our funding
  // entities; a purchased loan's clause was set by the correspondent's closing
  // entity at origination, so we POPULATE it here, then validate insurance/title
  // reference it. (loan-acquisitions.md §"Mortgagee clause".)
  //
  // POST /set_mortgagee/{dealId} — body: MortgageeClause
  //   { name, addressLine1, addressLine2, city, state, zipCode, isaoaAtima,
  //     verbatimClause, sourceDocumentId }
  // Returns the full updated deal (stored on deal.mortgagee; deal.mortgageeClause
  // is the computed formatted ISAOA/ATIMA string).
  setMortgagee: (dealId, payload) =>
    losHttp
      .post(`${BASE}/set_mortgagee/${dealId}`, payload)
      .then((r) => r.data),

  // POST /validate_mortgagee/{dealId} — optional body
  //   { documents: [{ docType, mortgageeName | mortgageeClauseText | name, sourceDocumentId }] }
  // Returns { dealId, expectedMortgagee, validated, checks: [{ source, policyId?,
  //   coverageType?, mortgageeFound, clausePresent?, matches, sourceDocumentId? }],
  //   notes }. Also persists mortgageeClauseValidated + notes on the deal.
  validateMortgagee: (dealId, payload) =>
    losHttp
      .post(`${BASE}/validate_mortgagee/${dealId}`, payload || {})
      .then((r) => r.data),

  // ── Conditions (deal-keyed; postclose has its OWN, not /checklists) ────────
  getConditions: (dealId) =>
    losHttp.get(`${BASE}/conditions/${dealId}`).then((r) => r.data),

  createCondition: (dealId, payload) =>
    losHttp
      .post(`${BASE}/create_condition/${dealId}`, payload)
      .then((r) => r.data),

  updateCondition: (dealId, conditionId, payload) =>
    losHttp
      .post(`${BASE}/update_condition/${dealId}/${conditionId}`, payload)
      .then((r) => r.data),

  // ── Task lists (derived from the shared repository + per-deal state) ───────
  // reviewPhase: post_close_full (default) | post_close_light | post_close_super_light
  getTaskTemplate: (dealId, reviewPhase) =>
    losHttp
      .get(`${BASE}/task_repository/for_loan/${dealId}`, {
        params: reviewPhase ? { review_phase: reviewPhase } : undefined,
      })
      .then((r) => r.data),

  getTaskList: (dealId) =>
    losHttp.get(`${BASE}/task_list/${dealId}`).then((r) => r.data),

  updateTaskList: (dealId, tasks) =>
    losHttp
      .post(`${BASE}/update_task_list/${dealId}`, { tasks })
      .then((r) => r.data),

  // ── Diligence notes (postclose analog of underwritingNotes) ───────────────
  saveDiligenceNotes: (dealId, payload) =>
    losHttp
      .post(`${BASE}/diligence_notes/${dealId}`, payload)
      .then((r) => r.data),

  // ── Wire / closing queue (shared queue, queueType: 'acquisition') ──────────
  getClosingQueue: () =>
    losHttp.get(`${BASE}/closing_queue`).then((r) => r.data),

  enqueueForWire: (dealId, payload) =>
    losHttp
      .post(`${BASE}/closing_queue/enqueue/${dealId}`, payload || {})
      .then((r) => r.data),

  releaseWire: (dealId, payload) =>
    losHttp
      .post(`${BASE}/closing_queue/wire_release/${dealId}`, payload || {})
      .then((r) => r.data),
};

export default losAcquisitionsApi;
